<template>
  <div class="executive-summary-kpi-strip mb-10 page-break-avoid">
    <executive-summary-kpi-column
      :value="kpi.users.total"
      :label="$t('reports.executiveSummaryReport.users.total')"
      :points="kpi.users.points"
      :delta="kpi.users.delta"
      :rows="userRows"
    />
    <executive-summary-kpi-column
      :value="kpi.devices.total"
      :label="$t('reports.executiveSummaryReport.devicesCovered')"
      :points="kpi.devices.points"
      :delta="kpi.devices.delta"
      :rows="deviceRows"
    />
    <executive-summary-kpi-column
      :value="kpi.tickets.total"
      :label="$t('reports.executiveSummaryReport.totalTickets')"
      :caption="autoRemediatedCaption"
      :rows="ticketRows"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import ExecutiveSummaryKpiColumn, {
  type ExecutiveSummaryKpiRow,
} from "@/components/reports/executive-summary/ExecutiveSummaryKpiColumn.vue";
import type { ExecutiveSummaryKpiViewModel } from "@/_store/reports/adapters";
import camelCase from "lodash/camelCase";
import { formatReportNumber, formatReportPercentValue } from "@/_helpers/reports";
import type { OsType } from "@/constants/devices";

export default defineComponent({
  components: { ExecutiveSummaryKpiColumn },
  props: {
    kpi: {
      type: Object as PropType<ExecutiveSummaryKpiViewModel>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const userRows = computed<ExecutiveSummaryKpiRow[]>(() => [
      {
        id: "protected",
        label: i18n.t("reports.executiveSummaryReport.users.protected"),
        value: props.kpi.users.protected,
      },
      {
        id: "unprotected",
        label: i18n.t("reports.executiveSummaryReport.users.unprotected"),
        value: props.kpi.users.unProtected,
      },
    ]);

    const deviceRows = computed<ExecutiveSummaryKpiRow[]>(() =>
      Object.entries(props.kpi.devices.types).map(([osName, count]) => {
        const outdated = props.kpi.devices.outdatedDevices?.[osName as OsType];
        return {
          id: osName,
          // camelCase, not toLowerCase: the locale keys are camelCased (`macOs`), so lowercasing
          // OsType.MAC_OS ("macOs") asks for a `macos` key that does not exist and renders the raw
          // path. This matches how the rest of the repo resolves these labels.
          label: i18n.t(`general.osVersion.${camelCase(osName)}`),
          value: count ?? 0,
          // Parentheses are part of the presentation and live here at the point of use,
          // not in the reusable ExecutiveSummaryKpiColumn component, which renders valueNote generically.
          valueNote: outdated
            ? `(${i18n.t("reports.executiveSummaryReport.devices.outdated", { n: formatReportNumber(outdated) })})`
            : undefined,
        };
      })
    );

    const ticketRows = computed<ExecutiveSummaryKpiRow[]>(() => [
      {
        id: "unprocessed",
        label: i18n.t("reports.executiveSummaryReport.tickets.unprocessed"),
        value: props.kpi.tickets.totalUnProcessed,
      },
    ]);

    const autoRemediatedCaption = computed(() => {
      const percent = props.kpi.tickets.autoRemediatedPercent;
      return percent === undefined
        ? undefined
        : i18n.t("reports.executiveSummaryReport.autoRemediated", {
            n: formatReportPercentValue(percent),
          });
    });

    return { userRows, deviceRows, ticketRows, autoRemediatedCaption };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-kpi-strip {
  // The whole strip is the page-break unit, not the individual column: html2pdf pushes an
  // element to the next page with a spacer div inserted before it, and a spacer inserted into a
  // grid becomes a grid item, consuming a cell instead of displacing the flow. The strip fits a
  // page comfortably, so moving it whole is both correct and simpler.
  //
  // Grid, not a flex row: `auto-fit` drops to two columns and then one instead of
  // overflowing, and `minmax(0, …)` lets a track shrink below its content width.
  // A plain flex row cannot do either — flex items default to `min-width: auto`,
  // so they refuse to shrink and the card (overflow: hidden) silently clips them.
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px 56px;
}
</style>
